import { createContext, ReactNode, useContext, useMemo, useState } from 'react';

import { usePrefetchRecords } from 'components/SPM/hooks/usePrefetchRecords';
import { usePrefetchDataFieldValues } from 'components/TQ/hooks/usePrefetchDataFieldValues';
import { MAX_PAGE_SIZE } from 'components/TQDataGrid/utils/constants';
import { TQTabView } from 'components/TQDataGrid/utils/grid-types';
import { useGetDataObjectSchemas } from 'components/TQModels/hooks/useDataObjectSchemas';
import { useNavigationData } from 'hooks/useNavigationData';
import { useTerritoryViewContext } from 'contexts/TerritoryViewContext';
import { GetDataFieldValuesQueryParams, TQPrefetchDataFieldValuesParams } from 'types/dataFieldValue';

type TQFPSParams = {
  [key in TQTabView]:
    | {
        columnFilter: GetDataFieldValuesQueryParams['columnFilter'];
        columnUuid: GetDataFieldValuesQueryParams['sortColumnUuid'];
        ascending: GetDataFieldValuesQueryParams['sortAscending'];
        page: GetDataFieldValuesQueryParams['page'];
        limit: GetDataFieldValuesQueryParams['limit'];
        offset: GetDataFieldValuesQueryParams['offset'];
        recordId: GetDataFieldValuesQueryParams['recordId'];
      }
    | undefined;
};

type PlanPrefetchBootstrapContextType = {
  tqFPSParams: TQFPSParams;
  setTQFPSParams: (params: TQFPSParams) => void;
  prefetchKeyParams: Record<
    'territories' | 'accounts' | 'overview_accounts' | 'overview_territories',
    TQPrefetchDataFieldValuesParams
  >;
  isPrefetchingTerritoriesGrid: boolean;
  isPrefetchingAccountsGrid: boolean;
};

const PlanPrefetchBootstrapContext = createContext<PlanPrefetchBootstrapContextType | undefined>(undefined);

export const usePlanPrefetchBootstrap = () => {
  const context = useContext(PlanPrefetchBootstrapContext);
  if (!context) {
    throw new Error('usePlanPrefetchBootstrap must be used within a PlanPrefetchBootstrapProvider');
  }
  return context;
};

type Props = {
  children: ReactNode;
};

export const PlanPrefetchBootstrapProvider = ({ children }: Props) => {
  const { tqModel } = useNavigationData();
  const { territoriesSchema, accountsSchema } = useGetDataObjectSchemas();
  const { rootNode, activeNode, selectedTerritoryViewId } = useTerritoryViewContext();
  const tqModelId = tqModel?.id;

  const [tqFPSParams, setTQFPSParams] = useState<TQFPSParams>({
    [TQTabView.ACCOUNTS]: undefined,
    [TQTabView.TERRITORIES]: undefined,
  });

  /**
   * Prepare prefetch key for the data field values data hook and spm data hook that fetch territory and account records
   */
  const prefetchKeyParams = useMemo(() => {
    const getParams = (
      keyType: 'territories' | 'accounts' | 'overview_accounts' | 'overview_territories',
    ): TQPrefetchDataFieldValuesParams => {
      const schemaConfig = {
        territories: {
          schema: territoriesSchema,
          tabView: TQTabView.TERRITORIES,
        },
        accounts: {
          schema: accountsSchema,
          tabView: TQTabView.ACCOUNTS,
        },
        overview_accounts: {
          schema: accountsSchema,
          tabView: TQTabView.TERRITORIES,
        },
        overview_territories: {
          schema: territoriesSchema,
          tabView: TQTabView.ACCOUNTS,
        },
      };

      const config = schemaConfig[keyType];
      const dataObjectSchemaId = config.schema?.id;
      const territoryId = activeNode?.id === rootNode?.id ? '' : activeNode?.id;

      if (!dataObjectSchemaId) {
        return { modelId: '', dataObjectSchemaId: '' };
      }

      const baseParams: TQPrefetchDataFieldValuesParams = {
        modelId: tqModelId,
        dataObjectSchemaId,
      };

      if (keyType === 'overview_accounts' || keyType === 'overview_territories') {
        return baseParams;
      }

      let fpsParams: TQFPSParams[keyof TQFPSParams] | undefined = tqFPSParams[config.tabView as keyof TQFPSParams];
      if (!fpsParams) {
        fpsParams = {
          limit: MAX_PAGE_SIZE,
          page: 0,
          ascending: true,
          columnFilter: undefined,
          columnUuid: undefined,
          offset: undefined,
          recordId: undefined,
        };
      }

      return {
        ...baseParams,
        columnFilter: fpsParams.columnFilter,
        sortColumnUuid: fpsParams.columnUuid,
        sortAscending: fpsParams.ascending,
        page: fpsParams.page,
        limit: fpsParams.limit,
        territoryId,
        recordId: fpsParams.recordId,
        ...(keyType === 'accounts' ? { territoryViewId: selectedTerritoryViewId } : {}),
      };
    };

    return {
      territories: getParams('territories'),
      accounts: getParams('accounts'),
      overview_accounts: getParams('overview_accounts'),
      overview_territories: getParams('overview_territories'),
    };
  }, [tqModelId, rootNode, activeNode, territoriesSchema, accountsSchema, tqFPSParams, selectedTerritoryViewId]);

  // TQ Grid specific prefetching tokens
  const { isPrefetching: isPrefetchingTerritoriesGrid } = usePrefetchDataFieldValues({
    params: prefetchKeyParams.territories,
  });
  const { isPrefetching: isPrefetchingAccountsGrid } = usePrefetchDataFieldValues({
    params: prefetchKeyParams.accounts,
  });

  // Tokens corresponding to data on the overview dashboard.
  usePrefetchDataFieldValues({ params: prefetchKeyParams.overview_territories, shouldNormalizeParams: false });
  usePrefetchDataFieldValues({ params: prefetchKeyParams.overview_accounts, shouldNormalizeParams: false });
  usePrefetchRecords();

  const contextValue = useMemo(
    () => ({
      tqFPSParams,
      setTQFPSParams,
      prefetchKeyParams,
      isPrefetchingTerritoriesGrid,
      isPrefetchingAccountsGrid,
    }),
    [tqFPSParams, setTQFPSParams, prefetchKeyParams, isPrefetchingTerritoriesGrid, isPrefetchingAccountsGrid],
  );

  return <PlanPrefetchBootstrapContext.Provider value={contextValue}>{children}</PlanPrefetchBootstrapContext.Provider>;
};
